import { InputField } from '@/components/form/InputField';
import PasswordField from '@/components/form/PasswordField';
import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { Form } from '@/components/primitives/Form';
import { withAuthGuard } from '@/hoc/withAuthGuard';
import { useAuth } from '@/hooks/useAuth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { createLoginSchema, type LoginValues } from '@/utils/zodSchema';
import { Button, Card, Typography } from '@fleet/webshop-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface LoginProps {}

const LoginComponent: FC<LoginProps> = () => {
  const { t } = useTranslation();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const [isPending, setIsPending] = useState(false);
  const { loginWithPassword } = useAuth();
  const navigate = useNavigate();

  usePageTitle('logIn');

  const form = useForm<LoginValues>({
    mode: 'onBlur',
    resolver: zodResolver(createLoginSchema(t)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = useCallback(
    async (values: LoginValues) => {
      setIsPending(true);
      try {
        await loginWithPassword(values);
        navigate('/purchase', { replace: true });
      } catch (error) {
        toast.error(
          <Typography variant="body1">
            <TransText i18nKey="loginFailed" />
            {error instanceof Error ? `: ${error.message}` : ''}
          </Typography>
        );
      } finally {
        setIsPending(false);
      }
    },
    [loginWithPassword, navigate]
  );

  return (
    <CenteredLayout>
      <div className="mx-auto flex w-full max-w-[410px] flex-col gap-8 p-4 tablet:my-auto laptop:p-0">
        <Typography variant={isLaptopOrBigger ? 'title' : 'mobile-title'}>
          <TransText i18nKey="logIn" />
        </Typography>
        <Card className="flex flex-col">
          <Form
            form={form}
            onSubmit={form.handleSubmit(onSubmit)}
            className={cn('flex flex-col gap-3 [&_input]:h-12', {
              '[&_input]:h-8': isLaptopOrBigger,
            })}
          >
            <InputField
              name="email"
              required
              placeholder={t('text.enterYourEmailAddress')}
              label={<TransText i18nKey="email" />}
            />
            <PasswordField
              name="password"
              required
              placeholder={t('text.enterYourPassword')}
              label={<TransText i18nKey="password" />}
            />
            <Button
              data-testid="login-submit-button"
              type="submit"
              className="mt-3"
              size="medium"
              isPending={isPending}
            >
              <TransText i18nKey="logIn" />
            </Button>
          </Form>
          <div className="mt-4 flex flex-col items-center">
            <Button
              data-testid="forgot-password-button"
              onClick={() => navigate('/reset-password')}
              variant="ghost"
              className="self-center"
            >
              <TransText i18nKey="forgotPassword" />
              &#63;
            </Button>
            <div className="flex items-center gap-3 self-center">
              <Typography variant="body1">
                <TransText i18nKey="notRegisteredYet" />
              </Typography>
              <Button
                variant="ghost"
                data-testid="create-an-account-button"
                className={cn('w-fit p-0', 'text-primary')}
                onClick={() => navigate('/signup')}
              >
                <TransText i18nKey="createAnAccount" />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </CenteredLayout>
  );
};

const Login = withAuthGuard('loginOrSignup')(LoginComponent);

export default Login;
